import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { auth, db } from "../../lib/firebase";
import { getBankFormConfig } from "../../lib/bankFormConfig";
import { onAuthStateChanged } from "firebase/auth";
import { doc, getDoc, updateDoc, collection, query, where, getDocs, orderBy, limit, onSnapshot } from "firebase/firestore";

import PageMeta from "../components/dashboard/PageMeta";
import SocialPageHeader from "../../components/layout/SocialPageHeader";
import SectionTabNav from "../../components/layout/SectionTabNav";
import { useRouter } from "next/router";
import useKycStatus from "../../hooks/useKycStatus";
import KycBanner from "../../components/KycBanner";
import EarningsToWalletTransfer from "../../components/EarningsToWalletTransfer";
import SocialCard from "../../components/ui/SocialCard";
import {
  DollarSign, AlertTriangle, Zap, Pencil, Lock, Settings, BarChart3,
  CheckCircle, Coins, CreditCard, RefreshCw, ExternalLink, Wallet
} from "lucide-react";

// 🔴 Stripe is disabled platform-wide while we move to a new card processor.
// While true: Stripe Connect prompt is hidden, all countries route through Wise
// or Crypto for payouts. Flip back to false when restored.
const STRIPE_DISABLED = false;

// 🔴 Crypto payouts hidden for now — flip back to true to expose the option +
// the wallet-address management section. Historical "Crypto" labels in the
// payout history list are unaffected (they describe past payouts).
const CRYPTO_PAYOUT_ENABLED = false;

export default function PayoutSettings() {
  const { t } = useTranslation();
  const router = useRouter();
  // Live KYC status — used to gate Request Payout (server-side gate is
  // still authoritative).
  const { isApproved: kycApproved } = useKycStatus();
  const [loading, setLoading] = useState(true);
  const [redirecting, setRedirecting] = useState(false);
  const [error, setError] = useState("");
  const [userData, setUserData] = useState(null);
  const [stripeAccountInfo, setStripeAccountInfo] = useState(null);
  const [payoutHistory, setPayoutHistory] = useState([]);
  const [calculatedEarnings, setCalculatedEarnings] = useState(null);
  const [payoutSettings, setPayoutSettings] = useState({
    minimumAmount: 10,
    payoutMethod: 'stripe',
    cryptoWalletAddress: '', // legacy single-address field (mirrors active chain)
    cryptoPayoutToken: 'USDC',
    cryptoChain: 'base', // 'ethereum' | 'base' | 'polygon' — preferred network
    // Per-chain wallet addresses. Each network can have a different address.
    cryptoWalletAddressesByChain: { ethereum: '', base: '', polygon: '' },
    // Per-token wallet addresses (legacy).
    cryptoWalletAddresses: { USDC: '', USDT: '' },
  });
  const [showCustomAmount, setShowCustomAmount] = useState(false);
  const [customPayoutAmount, setCustomPayoutAmount] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  // Bank details state (India uses IFSC, Bosnia/others use IBAN)
  const [indiaBankDetails, setIndiaBankDetails] = useState({
    accountNumber: '',
    confirmAccountNumber: '',
    ifscCode: '',
    accountHolderName: '',
    bankName: '',
  });
  const [wiseBankDetails, setWiseBankDetails] = useState({
    iban: '',
    confirmIban: '',
    swiftBic: '',
    accountHolderName: '',
    bankName: '',
  });
  const [bankAccountDetails, setBankAccountDetails] = useState({
    accountNumber: '',
    confirmAccountNumber: '',
    bankName: '',
    branchCode: '',
    swiftBic: '',
    accountHolderName: '',
    accountType: '', // CHECKING / SAVINGS — only used for US, Canada
  });
  const [savingBankDetails, setSavingBankDetails] = useState(false);
  const [showBankDetailsForm, setShowBankDetailsForm] = useState(false);
  const [bankDetailsTypeByCountry, setBankDetailsTypeByCountry] = useState({
    'India': 'ifsc',
    'IN': 'ifsc',
    'Bosnia and Herzegovina': 'iban',
    'BA': 'iban',
  });

  // Live ledger breakdown — populated by an onSnapshot listener on
  // expertEarnings. Drives every number on the page so what the maven
  // sees and what /api/request-payout will claim stay in sync.
  //   totalEarned = lifetime gross (paid + claimable + pending + reserved)
  //   claimable   = status='available' AND payoutId=null AND releasableAt<=now
  //                 OR legacy status='completed' rows (pre-lifecycle, no hold)
  //   pending     = status='pending' (still in 7-day hold)
  //   paid        = status='paid' (lifetime withdrawn through payouts)
  const [ledger, setLedger] = useState({
    totalEarned: 0,
    claimable: 0,
    pending: 0,
    paid: 0,
    earliestReleaseAt: null,
    loaded: false,
  });

  useEffect(() => {
    let userUnsubscribe = null;
    let payoutHistoryUnsubscribe = null;
    let earningsUnsubscribe = null;

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        setLoading(false);
        router.replace('/login');
        return;
      }

      try {
        userUnsubscribe = onSnapshot(doc(db, "users", user.uid), async (docSnapshot) => {
          if (!docSnapshot.exists()) return;

          const data = docSnapshot.data();
          setUserData({ ...data, uid: user.uid });

          if (data?.payoutSettings) {
            const savedChain = data.payoutSettings.cryptoChain ?? 'base';
            const legacyAddr = data.payoutSettings.cryptoWalletAddress ?? '';
            const savedAddrs = data.payoutSettings.cryptoWalletAddresses || {};
            const savedByChain = data.payoutSettings.cryptoWalletAddressesByChain || {};
            // If saved per-chain map has the same address in all three slots, that
            // came from a buggy migration — treat it as legacy single-address mode
            // and only populate the active chain's slot. Otherwise honor distinct
            // per-chain values, falling back to legacy for the active chain only.
            const allSame =
              savedByChain.ethereum &&
              savedByChain.ethereum === savedByChain.base &&
              savedByChain.base === savedByChain.polygon;
            const seedByChain = allSame ? {} : savedByChain;
            setPayoutSettings({
              // Force $10 minimum regardless of legacy Firestore value.
              // Older docs stored 50 from a prior default; UI has no widget to
              // edit this so once written it was stuck. Ignore the stored value
              // and always use 25.
              minimumAmount: 10,
              payoutMethod: data.payoutSettings.payoutMethod ?? 'stripe',
              cryptoWalletAddress: legacyAddr,
              cryptoPayoutToken: data.payoutSettings.cryptoPayoutToken ?? 'USDC',
              cryptoChain: savedChain,
              cryptoWalletAddresses: {
                USDC: savedAddrs.USDC || legacyAddr || '',
                USDT: savedAddrs.USDT || '',
              },
              cryptoWalletAddressesByChain: {
                ethereum: seedByChain.ethereum || (savedChain === 'ethereum' ? legacyAddr : '') || '',
                base: seedByChain.base || (savedChain === 'base' ? legacyAddr : '') || '',
                polygon: seedByChain.polygon || (savedChain === 'polygon' ? legacyAddr : '') || '',
              },
            });
          }

          // Load India bank details if available
          if (data?.indiaBankDetails) {
            setIndiaBankDetails({
              accountNumber: data.indiaBankDetails.accountNumber || '',
              confirmAccountNumber: data.indiaBankDetails.accountNumber || '',
              ifscCode: data.indiaBankDetails.ifscCode || '',
              accountHolderName: data.indiaBankDetails.accountHolderName || '',
              bankName: data.indiaBankDetails.bankName || '',
            });
          }
          // Load Wise bank details (IBAN countries like Bosnia)
          if (data?.wiseBankDetails) {
            setWiseBankDetails({
              iban: data.wiseBankDetails.iban || '',
              confirmIban: data.wiseBankDetails.iban || '',
              swiftBic: data.wiseBankDetails.swiftBic || '',
              accountHolderName: data.wiseBankDetails.accountHolderName || '',
              bankName: data.wiseBankDetails.bankName || '',
            });
            // Local-account countries (Ghana, Kenya, Indonesia, etc.) reuse the same Firestore field
            setBankAccountDetails({
              accountNumber: data.wiseBankDetails.accountNumber || '',
              confirmAccountNumber: data.wiseBankDetails.accountNumber || '',
              bankName: data.wiseBankDetails.bankName || '',
              branchCode: data.wiseBankDetails.branchCode || '',
              swiftBic: data.wiseBankDetails.swiftBic || '',
              accountHolderName: data.wiseBankDetails.accountHolderName || '',
              accountType: data.wiseBankDetails.accountType || '',
            });
          }

          // Load Stripe account info if connected
          if (data?.stripeAccountId) {
            await loadStripeAccountInfo(data.stripeAccountId);
          }

          // Calculate earnings from call logs if not in user data
          if (!data?.earnings || !data?.earnings?.total) {
            const calculatedData = await calculateEarningsFromCallLogs(user.uid);
            if (calculatedData) setCalculatedEarnings(calculatedData);
          }

          setLoading(false);
        });

        // Real-time listener for payout history from payoutRequests
        const payoutsQuery = query(
          collection(db, 'payoutRequests'),
          where('userId', '==', user.uid),
          orderBy('created', 'desc')
        );

        payoutHistoryUnsubscribe = onSnapshot(payoutsQuery, (snapshot) => {
          const payouts = snapshot.docs.map(doc => {
            const data = doc.data();
            return {
              id: doc.id,
              ...data,
              amount: data.amount || 0,
              status: data.status || 'pending',
              payoutMethod: data.payoutMethod || 'stripe',
              wiseTransferId: data.wiseTransferId || null,
              stripeTransferId: data.stripeTransferId || null,
              createdAt: data.created?.toDate ? data.created.toDate() : new Date(data.created || Date.now()),
              processedAt: data.processedAt?.toDate ? data.processedAt.toDate() : null,
            };
          });
          setPayoutHistory(payouts);
        });

        // Live ledger breakdown so the Available Balance card matches
        // exactly what /api/request-payout will claim. We listen to ALL
        // expertEarnings rows for this maven and compute claimable vs
        // pending client-side — the combination of (status, payoutId,
        // releasableAt) isn't index-friendly to query from the client.
        const earningsQuery = query(
          collection(db, 'expertEarnings'),
          where('expertId', '==', user.uid),
        );
        earningsUnsubscribe = onSnapshot(
          earningsQuery,
          (snapshot) => {
            const now = Date.now();
            let totalEarned = 0;
            let claimable = 0;
            let pending = 0;
            let paid = 0;
            let earliestReleaseAt = null;
            snapshot.forEach((d) => {
              const data = d.data() || {};
              const amount = Number(data.amount || 0);
              const lifetimeAmount = data.status === 'transferred'
                ? Number(data.transferredAmount ?? amount)
                : amount;
              const releasable =
                data.releasableAt?.toDate?.()
                || (data.releasableAt instanceof Date ? data.releasableAt : null)
                || (typeof data.releasableAt === 'string' ? new Date(data.releasableAt) : null);
              const releasableMs = releasable ? releasable.getTime() : null;

              // Lifetime gross excludes reversed rows (chargebacks).
              if (data.status !== 'reversed') {
                totalEarned += lifetimeAmount;
              }

              const releasedAndClaimable =
                data.status === 'available'
                && !data.payoutId
                && (releasableMs === null || releasableMs <= now);
              // Legacy rows pre-dating the lifecycle have status='completed'
              // and no releasableAt — count them as claimable since they're
              // long past the 7-day window.
              const legacyClaimable = data.status === 'completed' && !data.payoutId;
              // Treat pending-past-due rows as already-claimable so the UI
              // doesn't depend on the release cron firing on time. The
              // payout endpoint applies the same OR so the cron becomes
              // a tidy-up job rather than a hard gate on payouts.
              const pendingPastDue =
                data.status === 'pending' && releasableMs !== null && releasableMs <= now;

              if (releasedAndClaimable || legacyClaimable || pendingPastDue) {
                claimable += amount;
              } else if (data.status === 'pending') {
                // Still inside the 7-day hold.
                pending += amount;
                if (releasable && (!earliestReleaseAt || releasable < earliestReleaseAt)) {
                  earliestReleaseAt = releasable;
                }
              } else if (data.status === 'paid') {
                paid += amount;
              }
            });
            setLedger({
              totalEarned: Math.round(totalEarned * 100) / 100,
              claimable: Math.round(claimable * 100) / 100,
              pending: Math.round(pending * 100) / 100,
              paid: Math.round(paid * 100) / 100,
              earliestReleaseAt,
              loaded: true,
            });
          },
          (error) => {
            console.error('Error loading earnings ledger:', error);
            setLedger((prev) => ({ ...prev, loaded: true }));
          },
        );

      } catch (err) {
        console.error(err);
        setError(t("payout.errLoadInfo", "Failed to load payout info"));
        setLoading(false);
      }
    });

    return () => {
      unsubscribe();
      if (userUnsubscribe) userUnsubscribe();
      if (payoutHistoryUnsubscribe) payoutHistoryUnsubscribe();
      if (earningsUnsubscribe) earningsUnsubscribe();
    };
  }, []);

  // Maven role check
  useEffect(() => {
    if (userData && userData.role !== 'expert') {
      setError(t("payout.errAccessDenied", "Access Denied: Payout settings are only available for maven accounts."));
    }
  }, [userData?.role]);

  const calculateEarningsFromCallLogs = async (userId) => {
    try {
      const callLogsQuery = query(
        collection(db, "callLogs"),
        where("expertId", "==", userId)
      );
      const callLogsSnap = await getDocs(callLogsQuery);
      let totalEarnings = 0;

      callLogsSnap.forEach((doc) => {
        const call = doc.data();
        if (call.status === 'completed' || call.status === 'ended') {
          const callCost = Number(call.totalCost || call.cost || 0);
          const expertEarnings = Number(call.expertEarnings) || (callCost * 0.7);
          totalEarnings += expertEarnings;
        }
      });

      return { total: totalEarnings, pending: 0, paid: 0 };
    } catch (error) {
      console.error('Error calculating earnings:', error);
      return null;
    }
  };

  const loadStripeAccountInfo = async (stripeAccountId) => {
    try {
      const response = await fetch("/api/stripe/account-info", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ stripeAccountId }),
      });
      if (response.ok) {
        const accountInfo = await response.json();
        setStripeAccountInfo(accountInfo);
      }
    } catch (error) {
      console.error("Error loading Stripe account info:", error);
    }
  };

  const updatePayoutSettings = async (newSettings) => {
    const updatedSettings = { ...payoutSettings, ...newSettings };
    setPayoutSettings(updatedSettings);
    setSaving(true);
    setSaveSuccess(false);
    try {
      await updateDoc(doc(db, 'users', userData.uid), {
        payoutSettings: updatedSettings,
        updatedAt: new Date().toISOString()
      });
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (error) {
      console.error('Error updating payout settings:', error);
      setError(t("payout.errSaveSettings", "Failed to save settings. Please try again."));
    } finally {
      setSaving(false);
    }
  };

  const handleConnect = async () => {
    setRedirecting(true);
    try {
      const res = await fetch("/api/stripe/stripe-connect-link", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          uid: auth.currentUser?.uid,
          returnUrl: `${window.location.origin}/stripe/connect/success`,
          refreshUrl: `${window.location.origin}/stripe/connect/cancel`,
        }),
      });

      const data = await res.json();

      if (data.success && data.url) {
        window.location.href = data.url;
      } else if (data.action === 'redirect' && data.redirectUrl) {
        if (confirm(data.message + '\n\n' + t("payout.confirmUpdateProfile", "Would you like to update your profile now?"))) {
          window.location.href = data.redirectUrl;
        } else {
          setRedirecting(false);
        }
      } else {
        throw new Error(data?.error || data?.message || t("payout.errNoStripeUrl", "No Stripe URL returned"));
      }
    } catch (err) {
      console.error("Stripe Connect Error:", err);
      setError(err.message || t("payout.errCreateStripeLink", "Failed to create Stripe link"));
      setRedirecting(false);
    }
  };

  const refreshStripeStatus = async () => {
    if (!userData?.stripeAccountId) {
      alert(t("payout.errNoStripeAccount", "No Stripe account found to refresh"));
      return;
    }
    setLoading(true);
    try {
      const response = await fetch('/api/stripe/refresh-connect-status', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          uid: userData.uid,
          stripeAccountId: userData.stripeAccountId,
        }),
      });
      const data = await response.json();
      if (data.success) {
        alert(t("payout.stripeStatusRefreshed", "Stripe account status refreshed!"));
        window.location.reload();
      } else {
        throw new Error(data.error || t("payout.errRefreshStatus", "Failed to refresh status"));
      }
    } catch (error) {
      console.error('Refresh error:', error);
      alert(t("payout.errRefreshStripe", "Failed to refresh Stripe status. Please try again."));
    } finally {
      setLoading(false);
    }
  };

  const handleRequestPayout = async (useCustomAmount = false) => {
    // KYC gate — server still rejects un-verified experts in /api/request-payout,
    // but redirect on the client so the user lands in the verification flow
    // instead of seeing a generic 403.
    if (!kycApproved) {
      alert(t("payout.errKycRequired", "Identity verification is required before you can request a payout."));
      router.push('/settings/kyc');
      return;
    }

    const currentAvailableBalance = getAvailableBalance();
    let payoutAmount = currentAvailableBalance;

    if (useCustomAmount) {
      const customAmount = parseFloat(customPayoutAmount);
      if (isNaN(customAmount) || customAmount <= 0) {
        alert(t("payout.errInvalidAmount", "Please enter a valid payout amount."));
        return;
      }
      if (customAmount > currentAvailableBalance + 0.005) {
        alert(t("payout.errCustomExceeds", "Custom amount (${{amount}}) cannot exceed your available balance (${{balance}}).", { amount: customAmount.toFixed(2), balance: currentAvailableBalance.toFixed(2) }));
        return;
      }
      payoutAmount = customAmount;
    }

    if (payoutAmount < payoutSettings.minimumAmount) {
      alert(t("payout.errMinimumAmount", "Minimum payout amount is ${{minimum}}. You requested ${{requested}}.", { minimum: payoutSettings.minimumAmount.toFixed(2), requested: payoutAmount.toFixed(2) }));
      return;
    }

    // ✅ Auto-detect effective payout method based on what's actually configured.
    // The saved payoutSettings.payoutMethod is the user's preference but may be
    // a stale default ('wise'/'stripe') even when only crypto is configured.
    const _activeChainE = payoutSettings.cryptoChain || 'base';
    const _activeAddrE =
      payoutSettings.cryptoWalletAddressesByChain?.[_activeChainE] ||
      payoutSettings.cryptoWalletAddresses?.[payoutSettings.cryptoPayoutToken || 'USDC'] ||
      payoutSettings.cryptoWalletAddress;
    const _hasCryptoSetupE = !!_activeAddrE && _activeAddrE.startsWith('0x');
    const _isWiseUserE = !!(isIndianExpert || isIbanExpert || isBankAccountExpert);
    const _bankReadyE = _isWiseUserE
      ? (isIndianExpert ? !!userData?.indiaBankDetails?.accountNumber
        : isIbanExpert ? !!userData?.wiseBankDetails?.iban
        : !!userData?.wiseBankDetails?.accountNumber)
      : !!stripeAccountInfo?.payoutsEnabled;
    const _savedE = payoutSettings.payoutMethod;

    // Crypto payouts are DISABLED. Method is decided purely by country:
    // USA -> Stripe, all other (Wise-supported) countries -> Wise. Any account
    // still carrying a stale 'crypto' method is auto-healed to the correct one
    // below (this value is persisted back to the user doc). Crypto branches are
    // intentionally removed; the crypto code paths remain as disabled dead code.
    const effectivePayoutMethod = _isWiseUserE ? 'wise' : 'stripe';

    if (effectivePayoutMethod !== _savedE && userData?.uid) {
      try {
        await updateDoc(doc(db, 'users', userData.uid), {
          'payoutSettings.payoutMethod': effectivePayoutMethod,
          updatedAt: new Date().toISOString(),
        });
        setPayoutSettings((prev) => ({ ...prev, payoutMethod: effectivePayoutMethod }));
      } catch (e) {
        console.warn('[payout] Failed to persist auto-selected method:', e?.message);
      }
    }

    // Validate payout method
    if (effectivePayoutMethod === 'crypto') {
      const activeToken = payoutSettings.cryptoPayoutToken || 'USDC';
      const activeChain = payoutSettings.cryptoChain || 'base';
      const activeAddr =
        payoutSettings.cryptoWalletAddressesByChain?.[activeChain] ||
        payoutSettings.cryptoWalletAddresses?.[activeToken] ||
        payoutSettings.cryptoWalletAddress;
      if (!activeAddr || !activeAddr.startsWith('0x')) {
        const chainLabel = activeChain === 'ethereum' ? 'Ethereum' : activeChain === 'polygon' ? 'Polygon' : 'Base';
        alert(t("payout.errWalletRequired", "{{token}} wallet address required for {{chain}}. Please add your account address below.", { token: activeToken, chain: chainLabel }));
        return;
      }
    } else if (isIndianExpert) {
      if (!userData?.indiaBankDetails?.accountNumber) {
        alert(t("payout.errAddIndiaBank", "Please add your Indian bank account details to enable payouts."));
        return;
      }
    } else if (isIbanExpert) {
      if (!userData?.wiseBankDetails?.iban) {
        alert(t("payout.errAddIbanBank", "Please add your bank account details (IBAN) to enable payouts."));
        return;
      }
    } else if (isBankAccountExpert) {
      if (!userData?.wiseBankDetails?.accountNumber) {
        alert(t("payout.errAddBank", "Please add your bank account details to enable payouts."));
        return;
      }
    } else {
      if (!stripeAccountInfo?.payoutsEnabled) {
        alert(t("payout.errCompleteStripe", "Complete your Stripe Connect setup to enable payouts."));
        return;
      }
    }

    // Tax compliance check
    const totalEarnings = userData?.earnings?.total || calculatedEarnings?.total || 0;
    const taxInformation = userData?.taxInformation;
    if (totalEarnings >= 600 && (!taxInformation || !taxInformation.formCompleted)) {
      if (confirm(t("payout.confirmTaxInfo", "You need to complete your tax information before requesting payouts since your earnings exceed $600. Would you like to be redirected?"))) {
        window.location.href = '/settings/tax-documents';
        return;
      } else {
        return;
      }
    }

    const activeTokenForConfirm = payoutSettings.cryptoPayoutToken || 'USDC';
    const activeChainForConfirm = payoutSettings.cryptoChain || 'base';
    const activeAddrForConfirm = (
      payoutSettings.cryptoWalletAddressesByChain?.[activeChainForConfirm] ||
      payoutSettings.cryptoWalletAddresses?.[activeTokenForConfirm] ||
      payoutSettings.cryptoWalletAddress ||
      ''
    ).substring(0, 10);
    const payoutMethodText = effectivePayoutMethod === 'crypto'
      ? t("payout.methodTextCrypto", "Crypto ({{token}} to {{address}}...)", { token: activeTokenForConfirm, address: activeAddrForConfirm })
      : effectivePayoutMethod === 'wise'
        ? t("payout.methodTextWise", "Wise (Bank Transfer)")
        : t("payout.methodTextStripe", "Stripe (Bank Transfer)");

    const confirmMessage = useCustomAmount
      ? t("payout.confirmPayoutCustom", "Request payout of ${{amount}} via {{method}}?\n\nAvailable: ${{available}}", { amount: payoutAmount.toFixed(2), method: payoutMethodText, available: currentAvailableBalance.toFixed(2) })
      : t("payout.confirmPayoutFull", "Request full payout of ${{amount}} via {{method}}?", { amount: payoutAmount.toFixed(2), method: payoutMethodText });

    if (confirm(confirmMessage)) {
      setLoading(true);
      try {
        const idToken = await auth.currentUser?.getIdToken();
        if (!idToken) throw new Error('Please sign in again to request a payout.');
        const response = await fetch("/api/request-payout", {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${idToken}` },
          body: JSON.stringify({
            uid: userData.uid,
            customAmount: useCustomAmount ? payoutAmount : undefined,
            payoutMethod: effectivePayoutMethod,
            cryptoPayoutToken: payoutSettings.cryptoPayoutToken || 'USDC',
            cryptoWalletAddresses: payoutSettings.cryptoWalletAddresses || {},
            cryptoWalletAddressesByChain: payoutSettings.cryptoWalletAddressesByChain || {},
            cryptoChain: payoutSettings.cryptoChain || 'base',
          }),
        });

        if (response.ok) {
          alert(t("payout.payoutSubmitted", "Payout request submitted! Your ${{amount}} request is pending admin approval.", { amount: payoutAmount.toFixed(2) }));
          setShowCustomAmount(false);
          setCustomPayoutAmount('');
        } else {
          const error = await response.json();
          throw new Error(error.message || t("payout.errPayoutFailed", "Payout request failed"));
        }
      } catch (error) {
        alert(error.message || t("payout.errProcessPayout", "Failed to process payout"));
      } finally {
        setLoading(false);
      }
    }
  };

  // Save India bank details
  const saveIndiaBankDetails = async () => {
    if (!indiaBankDetails.accountNumber || !indiaBankDetails.ifscCode || !indiaBankDetails.accountHolderName || !indiaBankDetails.bankName) {
      alert(t("payout.errFillRequired", "Please fill in all required fields"));
      return;
    }
    if (indiaBankDetails.accountNumber !== indiaBankDetails.confirmAccountNumber) {
      alert(t("payout.errAccountMismatch", "Account numbers do not match"));
      return;
    }
    const ifscRegex = /^[A-Z]{4}0[A-Z0-9]{6}$/;
    if (!ifscRegex.test(indiaBankDetails.ifscCode.toUpperCase())) {
      alert(t("payout.errInvalidIfsc", "Please enter a valid IFSC code (e.g., SBIN0001234)"));
      return;
    }
    setSavingBankDetails(true);
    try {
      await updateDoc(doc(db, 'users', userData.uid), {
        indiaBankDetails: {
          accountNumber: indiaBankDetails.accountNumber,
          ifscCode: indiaBankDetails.ifscCode.toUpperCase(),
          accountHolderName: indiaBankDetails.accountHolderName,
          bankName: indiaBankDetails.bankName,
          updatedAt: new Date().toISOString(),
        },
        updatedAt: new Date().toISOString(),
      });
      alert(t("payout.indiaBankSaved", "✅ Bank details saved successfully! You can now request payouts."));
      setShowBankDetailsForm(false);
    } catch (error) {
      console.error('Error saving bank details:', error);
      alert(t("payout.errSaveBankIndia", "❌ Failed to save bank details. Please try again."));
    } finally {
      setSavingBankDetails(false);
    }
  };

  // Check if user is from India
  // Save Wise/IBAN bank details (Bosnia etc)
  const saveWiseBankDetails = async () => {
    if (!wiseBankDetails.iban || !wiseBankDetails.swiftBic || !wiseBankDetails.accountHolderName || !wiseBankDetails.bankName) {
      alert(t("payout.errFillRequired", "Please fill in all required fields"));
      return;
    }
    if (wiseBankDetails.iban !== wiseBankDetails.confirmIban) {
      alert(t("payout.errIbanMismatch", "IBAN numbers do not match"));
      return;
    }
    const ibanClean = wiseBankDetails.iban.replace(/\s/g, '').toUpperCase();
    if (!/^[A-Z]{2}\d{2}[A-Z0-9]{4,30}$/.test(ibanClean)) {
      alert(t("payout.errInvalidIban", "Please enter a valid IBAN"));
      return;
    }
    const swiftClean = wiseBankDetails.swiftBic.replace(/\s/g, '').toUpperCase();
    if (!/^[A-Z]{4}[A-Z]{2}[A-Z0-9]{2}([A-Z0-9]{3})?$/.test(swiftClean)) {
      alert(t("payout.errInvalidSwift", "Please enter a valid SWIFT/BIC code (8 or 11 characters)"));
      return;
    }
    setSavingBankDetails(true);
    try {
      await updateDoc(doc(db, 'users', userData.uid), {
        wiseBankDetails: {
          iban: ibanClean,
          swiftBic: swiftClean,
          accountHolderName: wiseBankDetails.accountHolderName,
          bankName: wiseBankDetails.bankName,
          updatedAt: new Date().toISOString(),
        },
        updatedAt: new Date().toISOString(),
      });
      alert(t("payout.bankSaved", "Bank details saved successfully!"));
      setShowBankDetailsForm(false);
    } catch (error) {
      console.error('Error saving bank details:', error);
      alert(t("payout.errSaveBank", "Failed to save bank details. Please try again."));
    } finally {
      setSavingBankDetails(false);
    }
  };

  // Save local bank account details (Ghana, Kenya, Indonesia, etc. — no IBAN)
  const saveBankAccountDetails = async () => {
    if (!bankAccountDetails.accountHolderName || !bankAccountDetails.bankName || !bankAccountDetails.accountNumber) {
      alert(t("payout.errFillAccountFields", "Please fill in Account Holder, Bank Name, and Account Number."));
      return;
    }
    if (bankAccountDetails.accountNumber !== bankAccountDetails.confirmAccountNumber) {
      alert(t("payout.errAccountMismatchDot", "Account numbers do not match."));
      return;
    }
    const acctClean = bankAccountDetails.accountNumber.replace(/\s/g, '');
    if (!/^[A-Za-z0-9]{4,30}$/.test(acctClean)) {
      alert(t("payout.errAccountFormat", "Account number should be 4–30 alphanumeric characters."));
      return;
    }
    const cfg = getBankFormConfig(userData?.country);
    const branchClean = (bankAccountDetails.branchCode || '').replace(/\s/g, '');
    if (cfg.branchCodeRequired && !branchClean) {
      alert(t("payout.errBranchRequired", "{{label}} is required.", { label: cfg.branchCodeLabel }));
      return;
    }
    if (branchClean && cfg.branchCodeRegex && !cfg.branchCodeRegex.test(branchClean)) {
      alert(cfg.branchCodeErrorMsg || t("payout.errBranchInvalid", "Invalid {{label}}.", { label: cfg.branchCodeLabel }));
      return;
    }
    if (cfg.requiresAccountType && !bankAccountDetails.accountType) {
      alert(t("payout.errSelectAccountType", "Please select an account type (Checking or Savings)."));
      return;
    }
    const swiftClean = (bankAccountDetails.swiftBic || '').replace(/\s/g, '').toUpperCase();
    if (swiftClean && !/^[A-Z]{4}[A-Z]{2}[A-Z0-9]{2}([A-Z0-9]{3})?$/.test(swiftClean)) {
      alert(t("payout.errSwiftFormat", "If provided, SWIFT/BIC must be 8 or 11 characters."));
      return;
    }
    setSavingBankDetails(true);
    try {
      await updateDoc(doc(db, 'users', userData.uid), {
        wiseBankDetails: {
          bankDetailsType: 'bank_account',
          accountNumber: acctClean,
          bankName: bankAccountDetails.bankName,
          branchCode: branchClean,
          swiftBic: swiftClean,
          accountHolderName: bankAccountDetails.accountHolderName,
          accountType: bankAccountDetails.accountType || '',
          country: userData?.country || '',
          updatedAt: new Date().toISOString(),
        },
        updatedAt: new Date().toISOString(),
      });
      alert(t("payout.bankSaved", "Bank details saved successfully!"));
      setShowBankDetailsForm(false);
    } catch (error) {
      console.error('Error saving bank details:', error);
      alert(t("payout.errSaveBank", "Failed to save bank details. Please try again."));
    } finally {
      setSavingBankDetails(false);
    }
  };

  // Countries that use Wise for payouts (fetched dynamically from API — supports 100+ countries)
  const [wiseCountryNames, setWiseCountryNames] = useState(['India', 'Bosnia and Herzegovina']);
  useEffect(() => {
    const apiUrl = process.env.NEXT_PUBLIC_API_URL || '';
    fetch(`${apiUrl}/api/public/payout-countries`)
      .then(res => res.json())
      .then(data => {
        if (data.wiseCountryNames?.length) setWiseCountryNames(data.wiseCountryNames);
        if (Array.isArray(data.countries)) {
          const map = {};
          for (const c of data.countries) {
            if (c?.bankDetailsType) {
              if (c.name) map[c.name] = c.bankDetailsType;
              if (c.code) map[c.code] = c.bankDetailsType;
            }
          }
          if (Object.keys(map).length) setBankDetailsTypeByCountry(prev => ({ ...prev, ...map }));
        }
      })
      .catch(() => {});
  }, []);
  const userBankDetailsType = bankDetailsTypeByCountry[userData?.country] ?? null;
  const isIndianExpert = userBankDetailsType === 'ifsc' || userData?.country === 'India' || userData?.country === 'IN';
  // While Stripe is disabled, every non-India country routes through Wise local bank transfer.
  const isWiseExpert = STRIPE_DISABLED || wiseCountryNames.includes(userData?.country) || isIndianExpert;
  const isIbanExpert = userBankDetailsType === 'iban';
  const isBankAccountExpert = isWiseExpert && !isIndianExpert && !isIbanExpert; // Ghana, Kenya, Indonesia, etc.
  const isBosniaExpert = isIbanExpert; // back-compat alias
  const hasIndiaBankDetails = !!userData?.indiaBankDetails?.accountNumber;
  const hasIbanBankDetails = !!userData?.wiseBankDetails?.iban;
  const hasAccountBankDetails = !!userData?.wiseBankDetails?.accountNumber;
  const hasWiseBankDetails = hasIbanBankDetails || hasAccountBankDetails;

  if (loading) return <p className="text-center mt-10">{t("payout.loading", "Loading...")}</p>;

  const mavenWalletTabs = [
    { label: t("payout.tabPayout", "Payout"), href: "/settings/payout" },
    { label: t("payout.tabTaxDocuments", "Tax Documents"), href: "/settings/tax-documents" },
  ];

  // Maven role access control
  if (userData && userData.role !== 'expert') {
    return (
      <div className="max-w-3xl mx-auto p-4">
        <PageMeta title="Access Denied - Payout Settings" description="Payout settings are only available for maven accounts." />
        <SocialPageHeader icon={<DollarSign className="w-6 h-6" />} title={t("payout.accessDeniedTitle", "Access Denied")} subtitle={t("payout.accessDeniedSubtitle", "Payout settings are restricted to maven accounts")} />
        <SocialCard>
          <div className="p-8 text-center">
            <p className="text-gray-600 dark:text-gray-400">{t("payout.mavenOnly", "This feature is only available for maven accounts.")}</p>
            <button onClick={() => window.history.back()} className="mt-4 px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors">
              {t("payout.goBack", "Go Back")}
            </button>
          </div>
        </SocialCard>
      </div>
    );
  }

  // Available balance now comes from the ledger (status='available' and
  // past the 7-day hold). Fall back to the user-doc delta only while the
  // first ledger snapshot is loading — that way the page never flashes $0
  // for a freshly mounted listener.
  const getAvailableBalance = () => {
    if (ledger.loaded) return ledger.claimable;
    if (userData?.earnings?.total !== undefined && userData?.earnings?.total !== null) {
      const calculated = userData.earnings.total - (userData.earnings.paid || 0);
      return Math.max(0, Math.round(calculated * 100) / 100);
    }
    if (calculatedEarnings?.total !== undefined && calculatedEarnings?.total !== null) {
      const calculated = calculatedEarnings.total - (calculatedEarnings.paid || 0);
      return Math.max(0, Math.round(calculated * 100) / 100);
    }
    return 0;
  };

  const availableBalance = getAvailableBalance();
  // While Stripe is disabled, treat no expert as "Stripe-connected" so the
  // Stripe Connect UI never renders even for users who previously connected.
  const isStripeConnected = !STRIPE_DISABLED && !!userData?.stripeAccountId && stripeAccountInfo?.detailsSubmitted;

  return (
    <div className="max-w-3xl mx-auto p-4">
      <PageMeta title="Maven Payout Settings" description="Manage your payouts and receive your earnings." />

      <SocialPageHeader
        icon={<DollarSign className="w-6 h-6" />}
        title={t("payout.headerTitle", "Payout")}
        subtitle={t("payout.headerSubtitle", "Manage how you receive your maven earnings")}
      />

      <SectionTabNav tabs={mavenWalletTabs} />

      {/* KYC banner — page-level prompt. Kept here (unlike dashboard/home,
          which were cleared 2026-07-14) because Payout is where KYC
          actually matters for mavens. Self-hides when approved. */}
      <KycBanner role="expert" />
      <EarningsToWalletTransfer compact />

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 p-4 rounded-2xl mb-6">
          {error}
        </div>
      )}

      {/* Tax Compliance Banner */}
      {(() => {
        const totalEarnings = userData?.totalEarnings || userData?.earnings?.total || calculatedEarnings?.total || 0;
        const taxInformation = userData?.taxInformation;
        const needsTaxInfo = totalEarnings >= 600 && (!taxInformation || !taxInformation.formCompleted);
        if (needsTaxInfo) {
          return (
            <SocialCard className="border-red-200 bg-red-50 mb-6">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5" />
                <div className="flex-1">
                  <h3 className="font-semibold text-red-800 dark:text-red-400 mb-2">{t("payout.taxInfoRequired", "Tax Information Required")}</h3>
                  <p className="text-red-700 dark:text-red-300 text-sm mb-3">
                    {t("payout.taxInfoExceed", "Your earnings exceed $600. Please complete your tax information before requesting payouts.")}
                  </p>
                  <button onClick={() => window.location.href = '/settings/tax-documents'} className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 text-sm font-medium">
                    {t("payout.completeTaxInfo", "Complete Tax Information")}
                  </button>
                </div>
              </div>
            </SocialCard>
          );
        }
        return null;
      })()}

      {/* Available Balance Card.
          Ledger-truth breakdown:
            Total Earnings = lifetime gross (paid + claimable + pending + reserved)
            Available for Payout = released, claimable right now
            Pending = still in the 7-day hold
            Paid Out = lifetime withdrawn via payouts
          All four numbers come from the same expertEarnings ledger that
          /api/request-payout claims from. */}
      <SocialCard className="mb-6 bg-gradient-to-r from-purple-500 to-purple-600 text-white border-purple-400">
        <div className="flex justify-between items-start gap-4">
          <div className="flex-1">
            <p className="text-purple-100 text-sm mb-1">{t("payout.totalEarnings", "Total Earnings")}</p>
            <p className="text-3xl font-bold">
              ${ledger.totalEarned.toFixed(2)}
              {(loading || !ledger.loaded) && <span className="text-sm ml-2 opacity-75">↻</span>}
            </p>
            <p className="text-purple-100 text-xs mt-1">{t("payout.lifetimeEarnings", "Lifetime earnings across calls & chat")}</p>

            <div className="mt-4 pt-3 border-t border-white/20">
              <p className="text-purple-100 text-xs mb-1">{t("payout.availableForPayout", "Available for Payout")}</p>
              <p className="text-2xl font-bold">${availableBalance.toFixed(2)}</p>
              <p className="text-purple-100 text-xs mt-1">{t("payout.availableHint", "Earnings older than 7 days, ready to withdraw")}</p>
            </div>

            {ledger.pending > 0 && (
              <div className="mt-3 pt-3 border-t border-white/20">
                <p className="text-purple-100 text-xs mb-1">{t("payout.pendingHold", "Pending (in 7-day hold)")}</p>
                <p className="text-lg font-semibold">${ledger.pending.toFixed(2)}</p>
                {ledger.earliestReleaseAt && (
                  <p className="text-purple-100 text-xs mt-1">
                    {t("payout.earliestRelease", "Earliest release: {{date}} ({{days}}d)", { date: new Date(ledger.earliestReleaseAt).toLocaleDateString(), days: Math.max(0, Math.ceil((new Date(ledger.earliestReleaseAt).getTime() - Date.now()) / 86400000)) })}
                  </p>
                )}
              </div>
            )}

            {ledger.paid > 0 && (
              <div className="mt-3 pt-3 border-t border-white/20">
                <p className="text-purple-100 text-xs mb-1">{t("payout.paidOutLifetime", "Paid Out (lifetime)")}</p>
                <p className="text-lg font-semibold">${ledger.paid.toFixed(2)}</p>
              </div>
            )}
          </div>
          <div className="flex flex-col gap-2 shrink-0">
            <button
              onClick={() => handleRequestPayout(false)}
              disabled={availableBalance < payoutSettings.minimumAmount || loading}
              className="bg-white/20 border border-white/30 text-white px-4 py-2 rounded-lg hover:bg-white/30 disabled:opacity-50 flex items-center gap-2 text-sm"
            >
              <Zap className="w-4 h-4" /> {t("payout.requestFullPayout", "Request Full Payout")}
            </button>
            <button
              onClick={() => setShowCustomAmount(!showCustomAmount)}
              disabled={availableBalance < payoutSettings.minimumAmount || loading}
              className="bg-white/10 border border-white/20 text-white px-4 py-1 rounded-lg hover:bg-white/20 disabled:opacity-50 flex items-center gap-2 text-xs"
            >
              <Pencil className="w-3 h-3" /> {t("payout.customAmount", "Custom Amount")}
            </button>
          </div>
        </div>

        {showCustomAmount && (
          <div className="mt-4 pt-4 border-t border-white/20">
            <div className="flex items-center gap-3">
              <div className="flex-1">
                <label className="block text-purple-100 text-sm mb-1">{t("payout.customPayoutAmount", "Custom Payout Amount")}</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">$</span>
                  <input
                    type="number"
                    step="0.01"
                    min={payoutSettings.minimumAmount}
                    max={availableBalance}
                    value={customPayoutAmount}
                    onChange={(e) => setCustomPayoutAmount(e.target.value)}
                    placeholder={t("payout.amountRangePlaceholder", "{{min}} - {{max}}", { min: payoutSettings.minimumAmount.toFixed(2), max: availableBalance.toFixed(2) })}
                    className="w-full pl-8 pr-4 py-2 bg-white/20 border border-white/30 rounded-lg text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-white/50"
                  />
                </div>
              </div>
              <div className="flex gap-2">
                <button onClick={() => handleRequestPayout(true)} disabled={!customPayoutAmount || loading} className="bg-white text-purple-600 px-4 py-2 rounded-lg hover:bg-white/90 disabled:opacity-50 font-medium text-sm">
                  {t("payout.request", "Request")}
                </button>
                <button onClick={() => { setShowCustomAmount(false); setCustomPayoutAmount(''); }} className="bg-white/20 border border-white/30 text-white px-3 py-2 rounded-lg hover:bg-white/30 text-sm">
                  {t("payout.cancel", "Cancel")}
                </button>
              </div>
            </div>
            <p className="text-purple-100 text-xs mt-2">
              {t("payout.minAvailable", "Min: ${{min}} | Available: ${{max}}", { min: payoutSettings.minimumAmount.toFixed(2), max: availableBalance.toFixed(2) })}
            </p>
          </div>
        )}
      </SocialCard>

      {/* Payout Method Selection */}
      <SocialCard className="mb-6">
        <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-200 mb-4 flex items-center gap-2">
          <Wallet className="w-5 h-5 text-purple-500" />
          {t("payout.payoutMethod", "Payout Method")}
        </h3>
        <div className={`grid grid-cols-1 ${CRYPTO_PAYOUT_ENABLED ? 'md:grid-cols-2' : ''} gap-4`}>
          {/* Bank Option — labels as Stripe for non-Wise countries, Wise/local bank for Wise countries */}
          <button
            onClick={() => updatePayoutSettings({ payoutMethod: 'stripe' })}
            className={`p-4 rounded-lg border-2 transition-all text-left ${
              payoutSettings.payoutMethod === 'stripe'
                ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                : 'border-gray-200 dark:border-gray-700 hover:border-gray-300'
            }`}
          >
            <CreditCard className={`w-8 h-8 mb-2 ${payoutSettings.payoutMethod === 'stripe' ? 'text-blue-600' : 'text-gray-400'}`} />
            <h4 className="font-semibold text-base mb-1">{isWiseExpert ? t("payout.bankWise", "Bank (Wise)") : t("payout.stripe", "Stripe")}</h4>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
              {isWiseExpert ? t("payout.localBankViaWise", "Local bank transfer via Wise") : t("payout.bankViaStripe", "Bank transfer via Stripe Connect")}
            </p>
            <div className="text-xs space-y-1">
              <p className="text-green-600 dark:text-green-400 flex items-center gap-1"><CheckCircle className="w-3 h-3" /> {isIndianExpert ? t("payout.indianBankAccount", "Indian bank account") : isIbanExpert ? t("payout.ibanAccount", "IBAN account") : isBankAccountExpert ? t("payout.localBankAccount", "Local bank account") : t("payout.directToBank", "Direct to bank account")}</p>
              <p className="text-green-600 dark:text-green-400 flex items-center gap-1"><CheckCircle className="w-3 h-3" /> {isWiseExpert ? t("payout.days35", "3-5 business days") : t("payout.days23", "2-3 business days")}</p>
              <p className="text-green-600 dark:text-green-400 flex items-center gap-1"><CheckCircle className="w-3 h-3" /> {isWiseExpert ? t("payout.adminApproval", "Admin approval") : t("payout.noFees", "No fees")}</p>
            </div>
          </button>

          {/* Crypto Option */}
          {CRYPTO_PAYOUT_ENABLED && (
            <button
              onClick={() => updatePayoutSettings({ payoutMethod: 'crypto' })}
              className={`p-4 rounded-lg border-2 transition-all text-left ${
                payoutSettings.payoutMethod === 'crypto'
                  ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20'
                  : 'border-gray-200 dark:border-gray-700 hover:border-gray-300'
              }`}
            >
              <Coins className={`w-8 h-8 mb-2 ${payoutSettings.payoutMethod === 'crypto' ? 'text-purple-600' : 'text-gray-400'}`} />
              <h4 className="font-semibold text-base mb-1">{t("payout.crypto", "Crypto")}</h4>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">{t("payout.usdcUsdtStablecoins", "USDC/USDT stablecoins")}</p>
              <div className="text-xs space-y-1">
                <p className="text-green-600 dark:text-green-400 flex items-center gap-1"><CheckCircle className="w-3 h-3" /> {t("payout.instantTransfer", "Instant transfer")}</p>
                <p className="text-green-600 dark:text-green-400 flex items-center gap-1"><CheckCircle className="w-3 h-3" /> {t("payout.globalAccess", "Global access")}</p>
                <p className="text-green-600 dark:text-green-400 flex items-center gap-1"><CheckCircle className="w-3 h-3" /> {t("payout.lowGasFees", "Low gas fees")}</p>
              </div>
            </button>
          )}
        </div>
      </SocialCard>

      {/* Stripe Connect Section — only for non-Wise countries on stripe method */}
      {payoutSettings.payoutMethod === 'stripe' && !isWiseExpert && (
        <SocialCard className="mb-6">
          <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-200 mb-4 flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-blue-500" />
            {t("payout.stripeConnect", "Stripe Connect")}
          </h3>

          {!isStripeConnected ? (
            <div className="space-y-4">
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {t("payout.connectStripeDesc", "Connect your Stripe account to receive payouts directly to your bank account.")}
              </p>
              <div className="flex gap-4 text-sm text-gray-500">
                <span className="flex items-center gap-1"><Lock className="w-4 h-4" /> {t("payout.bankLevelSecurity", "Bank-level security")}</span>
                <span className="flex items-center gap-1"><Zap className="w-4 h-4" /> {t("payout.fastPayouts", "Fast payouts")}</span>
              </div>
              <button
                onClick={handleConnect}
                disabled={redirecting}
                className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2 font-medium"
              >
                <ExternalLink className="w-4 h-4" />
                {redirecting ? t("payout.redirecting", "Redirecting...") : t("payout.connectWithStripe", "Connect with Stripe")}
              </button>

              {userData?.stripeAccountId && !isStripeConnected && (
                <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-lg">
                  <p className="text-sm text-yellow-700 dark:text-yellow-300 font-medium mb-1">{t("payout.setupIncomplete", "Setup Incomplete")}</p>
                  <p className="text-xs text-yellow-600 dark:text-yellow-400">
                    {t("payout.setupIncompleteDesc", "Your Stripe account is created but not fully set up. Click \"Connect with Stripe\" to complete onboarding.")}
                  </p>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              {/* Account Status */}
              <div className="grid grid-cols-3 gap-3">
                <div className={`p-3 rounded-lg text-center ${stripeAccountInfo?.chargesEnabled ? 'bg-green-50 dark:bg-green-900/20' : 'bg-red-50 dark:bg-red-900/20'}`}>
                  {stripeAccountInfo?.chargesEnabled
                    ? <CheckCircle className="w-5 h-5 text-green-600 mx-auto mb-1" />
                    : <AlertTriangle className="w-5 h-5 text-red-500 mx-auto mb-1" />
                  }
                  <p className="text-xs font-medium">{t("payout.charges", "Charges")}</p>
                  <p className="text-xs text-gray-500">{stripeAccountInfo?.chargesEnabled ? t("payout.enabled", "Enabled") : t("payout.disabled", "Disabled")}</p>
                </div>
                <div className={`p-3 rounded-lg text-center ${stripeAccountInfo?.payoutsEnabled ? 'bg-green-50 dark:bg-green-900/20' : 'bg-red-50 dark:bg-red-900/20'}`}>
                  {stripeAccountInfo?.payoutsEnabled
                    ? <CheckCircle className="w-5 h-5 text-green-600 mx-auto mb-1" />
                    : <AlertTriangle className="w-5 h-5 text-red-500 mx-auto mb-1" />
                  }
                  <p className="text-xs font-medium">{t("payout.payouts", "Payouts")}</p>
                  <p className="text-xs text-gray-500">{stripeAccountInfo?.payoutsEnabled ? t("payout.enabled", "Enabled") : t("payout.disabled", "Disabled")}</p>
                </div>
                <div className={`p-3 rounded-lg text-center ${stripeAccountInfo?.detailsSubmitted ? 'bg-green-50 dark:bg-green-900/20' : 'bg-red-50 dark:bg-red-900/20'}`}>
                  {stripeAccountInfo?.detailsSubmitted
                    ? <CheckCircle className="w-5 h-5 text-green-600 mx-auto mb-1" />
                    : <AlertTriangle className="w-5 h-5 text-red-500 mx-auto mb-1" />
                  }
                  <p className="text-xs font-medium">{t("payout.details", "Details")}</p>
                  <p className="text-xs text-gray-500">{stripeAccountInfo?.detailsSubmitted ? t("payout.complete", "Complete") : t("payout.incomplete", "Incomplete")}</p>
                </div>
              </div>

              {/* Requirements banner */}
              {userData?.stripeAccountInfo?.requirementsCurrentlyDue?.length > 0 && (
                <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-lg">
                  <p className="text-sm text-yellow-700 dark:text-yellow-300 font-medium mb-1">{t("payout.additionalVerification", "Additional Verification Required")}</p>
                  <ul className="text-xs text-yellow-600 dark:text-yellow-400 list-disc list-inside space-y-1">
                    {userData.stripeAccountInfo.requirementsCurrentlyDue.map((req, idx) => (
                      <li key={idx}>{req.replace(/_/g, ' ').replace(/\./g, ' > ')}</li>
                    ))}
                  </ul>
                  <button onClick={handleConnect} disabled={redirecting} className="mt-2 bg-yellow-600 hover:bg-yellow-700 text-white text-xs px-3 py-1.5 rounded-lg">
                    {t("payout.completeVerification", "Complete Verification")}
                  </button>
                </div>
              )}

              <button onClick={refreshStripeStatus} disabled={loading} className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700 disabled:opacity-50">
                <RefreshCw className="w-4 h-4" /> {t("payout.refreshStatus", "Refresh Status")}
              </button>
            </div>
          )}
        </SocialCard>
      )}

      {/* Crypto Wallet Section */}
      {CRYPTO_PAYOUT_ENABLED && payoutSettings.payoutMethod === 'crypto' && (
        <SocialCard className="mb-6">
          <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-200 mb-4 flex items-center gap-2">
            <Lock className="w-5 h-5 text-purple-500" />
            {t("payout.cryptoWalletAddress", "Crypto Wallet Address")}
          </h3>
          <div className="space-y-4">
            <p className="text-sm text-gray-500">
              {t("payout.cryptoWalletDesc", "Enter your ERC-20 wallet address and choose which stablecoin you want to receive your payouts in.")}
            </p>

            {/* Stablecoin selector */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                {t("payout.payoutStablecoin", "Payout Stablecoin")}
              </label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => updatePayoutSettings({ cryptoPayoutToken: 'USDC' })}
                  className={`p-3 rounded-lg border-2 transition-all flex items-center justify-center gap-2 ${
                    (payoutSettings.cryptoPayoutToken || 'USDC') === 'USDC'
                      ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300'
                      : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 text-gray-600 dark:text-gray-400'
                  }`}
                >
                  <span className="font-semibold">USDC</span>
                  <span className="text-xs">{t("payout.usdCoin", "USD Coin")}</span>
                </button>
                <button
                  type="button"
                  onClick={() => updatePayoutSettings({ cryptoPayoutToken: 'USDT' })}
                  className={`p-3 rounded-lg border-2 transition-all flex items-center justify-center gap-2 ${
                    payoutSettings.cryptoPayoutToken === 'USDT'
                      ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300'
                      : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 text-gray-600 dark:text-gray-400'
                  }`}
                >
                  <span className="font-semibold">USDT</span>
                  <span className="text-xs">{t("payout.tetherUsd", "Tether USD")}</span>
                </button>
              </div>
            </div>

            {/* Network selector */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                {t("payout.receiveOnNetwork", "Receive on network")}
              </label>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { key: 'base', label: 'Base', note: t("payout.gasLow", "low gas (~$0.01)") },
                  { key: 'polygon', label: 'Polygon', note: t("payout.gasLow", "low gas (~$0.01)") },
                  { key: 'ethereum', label: 'Ethereum', note: t("payout.gasHigh", "higher gas (~$5-30)") },
                ].map((c) => {
                  // USDT is not natively deployed on Base — disable Base when token is USDT
                  const tokenSym = payoutSettings.cryptoPayoutToken || 'USDC';
                  const disabled = c.key === 'base' && tokenSym === 'USDT';
                  const isActive = (payoutSettings.cryptoChain || 'base') === c.key;
                  return (
                    <button
                      key={c.key}
                      type="button"
                      disabled={disabled}
                      onClick={() => {
                        const newChainAddr = payoutSettings.cryptoWalletAddressesByChain?.[c.key] || '';
                        const updated = {
                          ...payoutSettings,
                          cryptoChain: c.key,
                          // Mirror the new chain's address to the legacy field so the
                          // payout request API stores the correct recipient.
                          cryptoWalletAddress: newChainAddr,
                        };
                        setPayoutSettings(updated);
                        updateDoc(doc(db, 'users', userData.uid), {
                          payoutSettings: updated,
                          updatedAt: new Date().toISOString(),
                        });
                      }}
                      style={isActive ? { borderColor: '#9333ea', backgroundColor: '#faf5ff', boxShadow: '0 0 0 3px rgba(147, 51, 234, 0.2)' } : undefined}
                      className={`relative p-3 rounded-lg border-2 text-left transition-all ${
                        isActive ? '' : 'border-gray-200 dark:border-gray-700 hover:border-gray-300'
                      } ${disabled ? 'opacity-40 cursor-not-allowed' : ''}`}
                    >
                      {isActive && (
                        <span className="absolute top-1 right-1 text-purple-600 text-sm">✓</span>
                      )}
                      <div className={`font-semibold text-sm ${isActive ? 'text-purple-700' : ''}`}>{c.label}</div>
                      <div className="text-[10px] text-gray-500 dark:text-gray-400 mt-0.5">{c.note}</div>
                      {disabled && <div className="text-[10px] text-red-500 mt-0.5">{t("payout.usdtNa", "USDT n/a")}</div>}
                    </button>
                  );
                })}
              </div>
            </div>

            {(() => {
              const activeToken = payoutSettings.cryptoPayoutToken || 'USDC';
              const activeChain = payoutSettings.cryptoChain || 'base';
              const chainLabel = activeChain === 'ethereum' ? 'Ethereum' : activeChain === 'polygon' ? 'Polygon' : 'Base';
              const activeAddr = payoutSettings.cryptoWalletAddressesByChain?.[activeChain] || '';
              const isValid = activeAddr.startsWith('0x');
              return (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                    {t("payout.walletAddressLabel", "{{token}} Wallet Address — {{chain}} network", { token: activeToken, chain: chainLabel })}
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={activeAddr}
                      onChange={(e) => setPayoutSettings({
                        ...payoutSettings,
                        cryptoWalletAddressesByChain: {
                          ...payoutSettings.cryptoWalletAddressesByChain,
                          [activeChain]: e.target.value,
                        },
                        // Mirror to legacy fields so older readers/APIs still see the active address
                        cryptoWalletAddress: e.target.value,
                        cryptoWalletAddresses: {
                          ...payoutSettings.cryptoWalletAddresses,
                          [activeToken]: e.target.value,
                        },
                      })}
                      placeholder={t("payout.walletAddressPlaceholder", "0x... (your {{chain}} wallet)", { chain: chainLabel })}
                      className="flex-1 px-4 py-3 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-purple-500 bg-white dark:bg-gray-900 dark:text-white font-mono text-sm"
                    />
                    <button
                      onClick={() => updatePayoutSettings({
                        cryptoWalletAddressesByChain: {
                          ...payoutSettings.cryptoWalletAddressesByChain,
                          [activeChain]: activeAddr,
                        },
                        cryptoWalletAddresses: {
                          ...payoutSettings.cryptoWalletAddresses,
                          [activeToken]: activeAddr,
                        },
                        cryptoWalletAddress: activeAddr,
                      })}
                      disabled={saving || !activeAddr || !isValid}
                      className="px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium disabled:opacity-50"
                    >
                      {saving ? t("payout.saving", "Saving...") : t("payout.save", "Save")}
                    </button>
                  </div>
                  {activeAddr && (
                    <button
                      type="button"
                      onClick={() => {
                        const updated = {
                          ...payoutSettings,
                          cryptoWalletAddressesByChain: {
                            ...payoutSettings.cryptoWalletAddressesByChain,
                            [activeChain]: '',
                          },
                          cryptoWalletAddress: '',
                        };
                        setPayoutSettings(updated);
                        updateDoc(doc(db, 'users', userData.uid), {
                          payoutSettings: updated,
                          updatedAt: new Date().toISOString(),
                        });
                      }}
                      className="mt-2 text-xs text-red-600 hover:text-red-700 underline"
                    >
                      {t("payout.clearAddressFor", "Clear address for {{chain}}", { chain: chainLabel })}
                    </button>
                  )}
                  {saveSuccess && (
                    <p className="mt-2 text-sm text-green-600 flex items-center gap-1">
                      <CheckCircle className="w-4 h-4" /> {t("payout.walletSaved", "{{token}} wallet address saved successfully!", { token: activeToken })}
                    </p>
                  )}
                  {!saveSuccess && activeAddr && isValid && (
                    <p className="mt-2 text-sm text-green-600 flex items-center gap-1">
                      <CheckCircle className="w-4 h-4" /> {t("payout.validWalletAddress", "Valid wallet address")}
                    </p>
                  )}
                  {activeAddr && !isValid && (
                    <p className="mt-2 text-sm text-red-600 flex items-center gap-1">
                      <AlertTriangle className="w-4 h-4" /> {t("payout.walletMustStart0x", "Wallet address must start with 0x")}
                    </p>
                  )}
                </div>
              );
            })()}
            {(() => {
              const ac = payoutSettings.cryptoChain || 'base';
              const acLabel = ac === 'ethereum' ? 'Ethereum' : ac === 'polygon' ? 'Polygon' : 'Base';
              const acNetwork = ac === 'ethereum' ? t("payout.ethereumMainnet", "Ethereum mainnet") : ac === 'polygon' ? t("payout.polygonNetwork", "Polygon network") : t("payout.baseNetwork", "Base network");
              return (
                <div className="p-3 bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-400 rounded text-xs text-blue-600 dark:text-blue-400 space-y-1">
                  <p className="font-semibold text-blue-700 dark:text-blue-300">
                    💡 {t("payout.youWillReceive", "You will receive {{token}} on {{chain}}", { token: payoutSettings.cryptoPayoutToken || 'USDC', chain: acLabel })}
                  </p>
                  <p>- {t("payout.ensureNetworkSupport", "Make sure your account address above supports")} <strong>{acNetwork}</strong></p>
                  <p>- {t("payout.walletsSupport", "Most wallets (MetaMask, Trust, Coinbase, Ledger) support all three networks")}</p>
                  <p>- {t("payout.noGasTokens", "We do NOT send ETH/POL/native gas tokens — only USDC/USDT stablecoins")}</p>
                  <p>- {t("payout.doubleCheckAddress", "Double-check your address -- crypto transfers cannot be reversed")}</p>
                </div>
              );
            })()}
          </div>
        </SocialCard>
      )}

      {/* India Bank Details Section */}
      {payoutSettings.payoutMethod !== 'crypto' && isIndianExpert && (
        <SocialCard className="mb-6 border-orange-200">
          <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-200 mb-4 flex items-center gap-2">
            🇮🇳 {t("payout.indiaBankTitle", "India Bank Details (Wise Transfer)")}
          </h3>

          {hasIndiaBankDetails && !showBankDetailsForm ? (
            <div className="space-y-3">
              <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4 mb-4">
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <span className="font-semibold text-green-800 dark:text-green-300">{t("payout.bankDetailsConfigured", "Bank Details Configured")}</span>
                </div>
                <p className="text-sm text-green-700 dark:text-green-400">
                  {t("payout.payoutsViaWiseIndia", "Payouts will be processed via Wise to your Indian bank account.")}
                </p>
              </div>

              <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">{t("payout.accountHolderColon", "Account Holder:")}</span>
                  <span className="font-medium">{userData?.indiaBankDetails?.accountHolderName}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">{t("payout.bankNameColon", "Bank Name:")}</span>
                  <span className="font-medium">{userData?.indiaBankDetails?.bankName}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">{t("payout.accountNumberColon", "Account Number:")}</span>
                  <span className="font-medium font-mono">****{userData?.indiaBankDetails?.accountNumber?.slice(-4)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">{t("payout.ifscCodeColon", "IFSC Code:")}</span>
                  <span className="font-medium font-mono">{userData?.indiaBankDetails?.ifscCode}</span>
                </div>
              </div>

              <button
                onClick={() => setShowBankDetailsForm(true)}
                className="text-sm text-orange-600 hover:text-orange-700 font-medium flex items-center gap-1"
              >
                <Pencil className="w-4 h-4" /> {t("payout.updateBankDetails", "Update Bank Details")}
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {t("payout.indiaIntro", "As an India-based expert, payouts are processed via Wise (formerly TransferWise) directly to your Indian bank account in INR.")}
              </p>

              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t("payout.accountHolderName", "Account Holder Name *")}</label>
                  <input
                    type="text"
                    value={indiaBankDetails.accountHolderName}
                    onChange={(e) => setIndiaBankDetails(prev => ({ ...prev, accountHolderName: e.target.value }))}
                    placeholder={t("payout.fullNamePlaceholder", "Full name as on bank account")}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 dark:bg-gray-800"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t("payout.bankNameLabel", "Bank Name *")}</label>
                  <input
                    type="text"
                    value={indiaBankDetails.bankName}
                    onChange={(e) => setIndiaBankDetails(prev => ({ ...prev, bankName: e.target.value }))}
                    placeholder={t("payout.bankNameIndiaPlaceholder", "e.g., State Bank of India")}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 dark:bg-gray-800"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t("payout.accountNumberLabel", "Account Number *")}</label>
                  <input
                    type="text"
                    value={indiaBankDetails.accountNumber}
                    onChange={(e) => setIndiaBankDetails(prev => ({ ...prev, accountNumber: e.target.value }))}
                    placeholder={t("payout.accountNumberIndiaPlaceholder", "Your bank account number")}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 dark:bg-gray-800 font-mono"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t("payout.confirmAccountNumber", "Confirm Account Number *")}</label>
                  <input
                    type="text"
                    value={indiaBankDetails.confirmAccountNumber}
                    onChange={(e) => setIndiaBankDetails(prev => ({ ...prev, confirmAccountNumber: e.target.value }))}
                    placeholder={t("payout.reEnterAccountNumber", "Re-enter account number")}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 dark:bg-gray-800 font-mono"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t("payout.ifscCodeLabel", "IFSC Code *")}</label>
                  <input
                    type="text"
                    value={indiaBankDetails.ifscCode}
                    onChange={(e) => setIndiaBankDetails(prev => ({ ...prev, ifscCode: e.target.value.toUpperCase() }))}
                    placeholder={t("payout.ifscPlaceholder", "e.g., SBIN0001234")}
                    maxLength={11}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 dark:bg-gray-800 font-mono uppercase"
                  />
                  <p className="text-xs text-gray-400 mt-1">{t("payout.ifscHelp", "11 characters — 4 letters + 0 + 6 alphanumeric")}</p>
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={saveIndiaBankDetails}
                  disabled={savingBankDetails}
                  className="bg-orange-600 text-white px-6 py-2 rounded-lg hover:bg-orange-700 disabled:opacity-50 font-medium text-sm"
                >
                  {savingBankDetails ? t("payout.saving", "Saving...") : hasIndiaBankDetails ? t("payout.updateBankDetails", "Update Bank Details") : t("payout.saveBankDetails", "Save Bank Details")}
                </button>
                {hasIndiaBankDetails && (
                  <button
                    onClick={() => setShowBankDetailsForm(false)}
                    className="bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 px-4 py-2 rounded-lg hover:bg-gray-300 text-sm"
                  >
                    {t("payout.cancel", "Cancel")}
                  </button>
                )}
              </div>

              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3 mt-3">
                <p className="text-xs text-blue-700 dark:text-blue-300">
                  💡 <strong>{t("payout.howItWorks", "How it works:")}</strong> {t("payout.howItWorksIndia", "After you request a payout, admin will transfer USD via Wise to your Indian bank account. Wise converts USD → INR at the best exchange rate. Transfers typically arrive within 1-2 business days.")}
                </p>
              </div>
            </div>
          )}
        </SocialCard>
      )}

      {/* IBAN Wise Countries Bank Details Section (Bosnia, Georgia, Iceland, Israel, etc.) */}
      {payoutSettings.payoutMethod !== 'crypto' && isIbanExpert && (
        <SocialCard className="mb-6 border-blue-200">
          <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-200 mb-4 flex items-center gap-2">
            🏦 {t("payout.bankTitle", "Bank Details (Wise Transfer)")}
          </h3>

          {hasIbanBankDetails && !showBankDetailsForm ? (
            <div className="space-y-3">
              <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4 mb-4">
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <span className="font-semibold text-green-800 dark:text-green-300">{t("payout.bankDetailsConfigured", "Bank Details Configured")}</span>
                </div>
                <p className="text-sm text-green-700 dark:text-green-400">
                  {t("payout.payoutsViaWiseBank", "Payouts will be processed via Wise to your bank account.")}
                </p>
              </div>
              <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 space-y-2">
                <div className="flex justify-between text-sm"><span className="text-gray-500">{t("payout.accountHolderColon", "Account Holder:")}</span><span className="font-medium">{userData?.wiseBankDetails?.accountHolderName}</span></div>
                <div className="flex justify-between text-sm"><span className="text-gray-500">{t("payout.bankNameColon", "Bank Name:")}</span><span className="font-medium">{userData?.wiseBankDetails?.bankName}</span></div>
                <div className="flex justify-between text-sm"><span className="text-gray-500">{t("payout.ibanColon", "IBAN:")}</span><span className="font-medium font-mono">****{userData?.wiseBankDetails?.iban?.slice(-4)}</span></div>
                <div className="flex justify-between text-sm"><span className="text-gray-500">{t("payout.swiftBicColon", "SWIFT/BIC:")}</span><span className="font-medium font-mono">{userData?.wiseBankDetails?.swiftBic}</span></div>
              </div>
              <button onClick={() => setShowBankDetailsForm(true)} className="text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1">
                <Pencil className="w-4 h-4" /> {t("payout.updateBankDetails", "Update Bank Details")}
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {t("payout.payoutsViaWiseDirectBank", "Payouts are processed via Wise directly to your bank account.")}
              </p>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t("payout.accountHolderName", "Account Holder Name *")}</label>
                  <input type="text" value={wiseBankDetails.accountHolderName}
                    onChange={(e) => setWiseBankDetails(prev => ({ ...prev, accountHolderName: e.target.value }))}
                    placeholder={t("payout.fullNamePlaceholder", "Full name as on bank account")}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-800" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t("payout.bankNameLabel", "Bank Name *")}</label>
                  <input type="text" value={wiseBankDetails.bankName}
                    onChange={(e) => setWiseBankDetails(prev => ({ ...prev, bankName: e.target.value }))}
                    placeholder={t("payout.bankNameIbanPlaceholder", "e.g., UniCredit Bank")}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-800" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t("payout.ibanLabel", "IBAN *")}</label>
                  <input type="text" value={wiseBankDetails.iban}
                    onChange={(e) => setWiseBankDetails(prev => ({ ...prev, iban: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '') }))}
                    placeholder={t("payout.ibanPlaceholder", "e.g., BA391234567890123456")}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 font-mono uppercase" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t("payout.confirmIban", "Confirm IBAN *")}</label>
                  <input type="text" value={wiseBankDetails.confirmIban}
                    onChange={(e) => setWiseBankDetails(prev => ({ ...prev, confirmIban: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '') }))}
                    placeholder={t("payout.reEnterIban", "Re-enter IBAN")}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 font-mono uppercase ${
                      wiseBankDetails.confirmIban && wiseBankDetails.iban !== wiseBankDetails.confirmIban ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                    }`} />
                  {wiseBankDetails.confirmIban && wiseBankDetails.iban !== wiseBankDetails.confirmIban && (
                    <p className="text-red-500 text-xs mt-1">{t("payout.ibanMismatch", "IBAN numbers do not match")}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t("payout.swiftBicLabel", "SWIFT/BIC Code *")}</label>
                  <input type="text" value={wiseBankDetails.swiftBic}
                    onChange={(e) => setWiseBankDetails(prev => ({ ...prev, swiftBic: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 11) }))}
                    placeholder={t("payout.swiftIbanPlaceholder", "e.g., UNICBABA")}
                    maxLength={11}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 font-mono uppercase" />
                </div>
              </div>
              <div className="flex gap-3">
                <button onClick={saveWiseBankDetails} disabled={savingBankDetails}
                  className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 font-medium text-sm">
                  {savingBankDetails ? t("payout.saving", "Saving...") : hasIbanBankDetails ? t("payout.updateBankDetails", "Update Bank Details") : t("payout.saveBankDetails", "Save Bank Details")}
                </button>
                {hasIbanBankDetails && (
                  <button onClick={() => setShowBankDetailsForm(false)}
                    className="bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 px-4 py-2 rounded-lg hover:bg-gray-300 text-sm">
                    {t("payout.cancel", "Cancel")}
                  </button>
                )}
              </div>
            </div>
          )}
        </SocialCard>
      )}

      {/* Local Bank Account Wise Countries (Ghana, Kenya, Indonesia, etc.) */}
      {payoutSettings.payoutMethod !== 'crypto' && isBankAccountExpert && (
        <SocialCard className="mb-6 border-blue-200">
          <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-200 mb-4 flex items-center gap-2">
            🏦 {t("payout.bankTitle", "Bank Details (Wise Transfer)")}
          </h3>

          {hasAccountBankDetails && !showBankDetailsForm ? (
            <div className="space-y-3">
              <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4 mb-4">
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <span className="font-semibold text-green-800 dark:text-green-300">{t("payout.bankDetailsConfigured", "Bank Details Configured")}</span>
                </div>
                <p className="text-sm text-green-700 dark:text-green-400">
                  {t("payout.payoutsViaWiseLocal", "Payouts will be processed via Wise to your local bank account.")}
                </p>
              </div>
              <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 space-y-2">
                <div className="flex justify-between text-sm"><span className="text-gray-500">{t("payout.accountHolderColon", "Account Holder:")}</span><span className="font-medium">{userData?.wiseBankDetails?.accountHolderName}</span></div>
                <div className="flex justify-between text-sm"><span className="text-gray-500">{t("payout.bankNameColon", "Bank Name:")}</span><span className="font-medium">{userData?.wiseBankDetails?.bankName}</span></div>
                <div className="flex justify-between text-sm"><span className="text-gray-500">{t("payout.accountNumberColon", "Account Number:")}</span><span className="font-medium font-mono">****{userData?.wiseBankDetails?.accountNumber?.slice(-4)}</span></div>
                {userData?.wiseBankDetails?.branchCode ? (
                  <div className="flex justify-between text-sm"><span className="text-gray-500">{t("payout.branchSortCodeColon", "Branch / Sort Code:")}</span><span className="font-medium font-mono">{userData?.wiseBankDetails?.branchCode}</span></div>
                ) : null}
                {userData?.wiseBankDetails?.swiftBic ? (
                  <div className="flex justify-between text-sm"><span className="text-gray-500">{t("payout.swiftBicColon", "SWIFT/BIC:")}</span><span className="font-medium font-mono">{userData?.wiseBankDetails?.swiftBic}</span></div>
                ) : null}
              </div>
              <button onClick={() => setShowBankDetailsForm(true)} className="text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1">
                <Pencil className="w-4 h-4" /> {t("payout.updateBankDetails", "Update Bank Details")}
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {t("payout.payoutsViaWiseDirectLocal", "Payouts are processed via Wise directly to your local bank account.")}
              </p>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t("payout.accountHolderName", "Account Holder Name *")}</label>
                  <input type="text" value={bankAccountDetails.accountHolderName}
                    onChange={(e) => setBankAccountDetails(prev => ({ ...prev, accountHolderName: e.target.value }))}
                    placeholder={t("payout.fullNamePlaceholder", "Full name as on bank account")}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-800" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t("payout.bankNameLabel", "Bank Name *")}</label>
                  <input type="text" value={bankAccountDetails.bankName}
                    onChange={(e) => setBankAccountDetails(prev => ({ ...prev, bankName: e.target.value }))}
                    placeholder={t("payout.bankNameLocalPlaceholder", "e.g., Standard Chartered Ghana")}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-800" />
                </div>
                {(() => {
                  const cfg = getBankFormConfig(userData?.country);
                  return (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t("payout.accountNumberLabel", "Account Number *")}</label>
                        <input type="text" value={bankAccountDetails.accountNumber}
                          onChange={(e) => setBankAccountDetails(prev => ({ ...prev, accountNumber: e.target.value.replace(/[^A-Za-z0-9]/g, '') }))}
                          placeholder={cfg.accountNumberPlaceholder || t("payout.localAccountNumberPlaceholder", "Local bank account number")}
                          className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 font-mono" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t("payout.confirmAccountNumber", "Confirm Account Number *")}</label>
                        <input type="text" value={bankAccountDetails.confirmAccountNumber}
                          onChange={(e) => setBankAccountDetails(prev => ({ ...prev, confirmAccountNumber: e.target.value.replace(/[^A-Za-z0-9]/g, '') }))}
                          placeholder={t("payout.reEnterAccountNumber", "Re-enter account number")}
                          className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 font-mono ${
                            bankAccountDetails.confirmAccountNumber && bankAccountDetails.accountNumber !== bankAccountDetails.confirmAccountNumber ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                          }`} />
                        {bankAccountDetails.confirmAccountNumber && bankAccountDetails.accountNumber !== bankAccountDetails.confirmAccountNumber && (
                          <p className="text-red-500 text-xs mt-1">{t("payout.accountMismatch", "Account numbers do not match")}</p>
                        )}
                      </div>
                      {cfg.requiresAccountType && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{cfg.accountTypeLabel} *</label>
                          <select
                            value={bankAccountDetails.accountType || ''}
                            onChange={(e) => setBankAccountDetails(prev => ({ ...prev, accountType: e.target.value }))}
                            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-800"
                          >
                            <option value="">{t("payout.selectAccountType", "Select account type…")}</option>
                            {cfg.accountTypeOptions.map(opt => (
                              <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                          </select>
                        </div>
                      )}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          {cfg.branchCodeLabel}{cfg.branchCodeRequired ? ' *' : ''}
                        </label>
                        <input type="text" value={bankAccountDetails.branchCode}
                          onChange={(e) => setBankAccountDetails(prev => ({ ...prev, branchCode: e.target.value }))}
                          placeholder={cfg.branchCodePlaceholder}
                          maxLength={cfg.branchCodeMaxLength}
                          className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 font-mono" />
                        {cfg.branchCodeHelp && <p className="text-xs text-gray-500 mt-1">{cfg.branchCodeHelp}</p>}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t("payout.swiftBicOptional", "SWIFT/BIC Code (optional)")}</label>
                        <input type="text" value={bankAccountDetails.swiftBic}
                          onChange={(e) => setBankAccountDetails(prev => ({ ...prev, swiftBic: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 11) }))}
                          placeholder={t("payout.swiftLocalPlaceholder", "e.g., SCBLGHAC")}
                          maxLength={11}
                          className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 font-mono uppercase" />
                        <p className="text-xs text-gray-400 mt-1">{cfg.swiftHelp || t("payout.swiftHelp", "Helps speed up international transfers (optional).")}</p>
                      </div>
                    </>
                  );
                })()}
              </div>
              <div className="flex gap-3">
                <button onClick={saveBankAccountDetails} disabled={savingBankDetails}
                  className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 font-medium text-sm">
                  {savingBankDetails ? t("payout.saving", "Saving...") : hasAccountBankDetails ? t("payout.updateBankDetails", "Update Bank Details") : t("payout.saveBankDetails", "Save Bank Details")}
                </button>
                {hasAccountBankDetails && (
                  <button onClick={() => setShowBankDetailsForm(false)}
                    className="bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 px-4 py-2 rounded-lg hover:bg-gray-300 text-sm">
                    {t("payout.cancel", "Cancel")}
                  </button>
                )}
              </div>
            </div>
          )}
        </SocialCard>
      )}

      {/* Payout Preferences */}
      <SocialCard className="mb-6">
        <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-200 mb-4 flex items-center gap-2">
          <Settings className="w-5 h-5 text-purple-500" />
          {t("payout.payoutPreferences", "Payout Preferences")}
        </h3>
        <div className="flex justify-between items-center py-3">
          <div>
            <p className="font-medium">{t("payout.minimumPayoutAmount", "Minimum Payout Amount")}</p>
            <p className="text-sm text-gray-500">{t("payout.minimumRequired", "${{amount}} minimum required", { amount: payoutSettings.minimumAmount.toFixed(2) })}</p>
          </div>
          <span className="font-semibold text-purple-600">${payoutSettings.minimumAmount.toFixed(2)}</span>
        </div>
        <div className="flex justify-between items-center py-3 border-t border-gray-200 dark:border-gray-700">
          <div>
            <p className="font-medium">{t("payout.currentMethod", "Current Method")}</p>
            <p className="text-sm text-gray-500">
              {CRYPTO_PAYOUT_ENABLED && payoutSettings.payoutMethod === 'crypto'
                ? t("payout.methodCryptoLong", "Crypto (USDC/USDT Stablecoins)")
                : isWiseExpert
                  ? t("payout.methodBankWiseLong", "Bank Transfer (via Wise)")
                  : t("payout.methodStripeLong", "Stripe (Bank Transfer)")}
            </p>
          </div>
          <span className={`px-3 py-1 rounded-full text-xs font-medium ${
            (!CRYPTO_PAYOUT_ENABLED || payoutSettings.payoutMethod !== 'crypto') ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'
          }`}>
            {CRYPTO_PAYOUT_ENABLED && payoutSettings.payoutMethod === 'crypto' ? t("payout.crypto", "Crypto") : isWiseExpert ? t("payout.wise", "Wise") : t("payout.stripe", "Stripe")}
          </span>
        </div>
      </SocialCard>

      {/* Payout History */}
      <SocialCard className="mb-8">
        <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-200 mb-4 flex items-center gap-2">
          <BarChart3 className="w-5 h-5 text-purple-500" />
          {t("payout.payoutHistory", "Payout History")}
        </h3>
        {payoutHistory.length === 0 ? (
          <div className="text-center py-8">
            <Coins className="w-8 h-8 text-gray-400 mx-auto mb-2" />
            <p className="text-gray-500 mb-2">{t("payout.noPayoutsYet", "No Payouts Yet")}</p>
            <p className="text-sm text-gray-400">
              {t("payout.noPayoutsDesc", "Your payout history will appear here once you request your first payout.")}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {payoutHistory.map((payout) => (
              <div key={payout.id} className="flex justify-between items-center py-3 border-b border-gray-200 dark:border-gray-700 last:border-b-0">
                <div>
                  <p className="font-medium">${payout.amount?.toFixed(2) || '0.00'}</p>
                  <p className="text-sm text-gray-500">
                    {payout.createdAt instanceof Date ? payout.createdAt.toLocaleDateString() : t("payout.dateUnavailable", "Date unavailable")}
                    {payout.payoutMethod && ` • ${payout.payoutMethod === 'wise' ? t("payout.wise", "Wise") : payout.payoutMethod === 'crypto' ? t("payout.crypto", "Crypto") : t("payout.stripe", "Stripe")}`}
                  </p>
                  {payout.wiseTransferId && (
                    <p className="text-xs text-gray-400">Wise #{payout.wiseTransferId}</p>
                  )}
                  {payout.stripeTransferId && (
                    <p className="text-xs text-gray-400">Stripe: {payout.stripeTransferId}</p>
                  )}
                </div>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                  payout.status === 'processed' || payout.status === 'completed' ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400' :
                  payout.status === 'approved' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400' :
                  payout.status === 'pending' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400' :
                  payout.status === 'cancelled' ? 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400' :
                  'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400'
                }`}>
                  {payout.status === 'processed' ? t("payout.statusProcessed", "Processed") : payout.status?.charAt(0).toUpperCase() + payout.status?.slice(1) || t("payout.statusUnknown", "Unknown")}
                </span>
              </div>
            ))}
          </div>
        )}
      </SocialCard>
    </div>
  );
}
